const NOTIFICATIONS_STORAGE_KEY = 'afn-ally-notifications-enabled';
let serviceWorkerRegistrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

// D7-05 (FE-6.4): every message used to share the single 'ally-message' tag,
// so a new notification silently replaced the previous one even when it came
// from a different conversation. Tags are now per-conversation so parallel
// conversations stack as separate notifications, and clicking one (or its
// "Open conversation" action) deep-links back to that conversation.
export const DEFAULT_NOTIFICATION_TAG = 'ally-message';

export function getConversationNotificationTag(conversationId?: string | null) {
  const normalized = String(conversationId || '').trim();
  return normalized ? `ally-conversation-${normalized}` : DEFAULT_NOTIFICATION_TAG;
}

export function getConversationNotificationUrl(conversationId?: string | null) {
  const normalized = String(conversationId || '').trim();
  return normalized ? `/?conversation=${encodeURIComponent(normalized)}` : '/';
}

export interface ShowNotificationConfig {
  force?: boolean;
  /** Notification grouping key; defaults to the shared legacy tag. */
  tag?: string;
  /** Same-origin URL opened when the notification (or its Open action) is clicked. */
  url?: string;
}

export function areNotificationsEnabled() {
  if (typeof window === 'undefined') return false;
  const stored = window.localStorage.getItem(NOTIFICATIONS_STORAGE_KEY);
  return stored === null ? true : stored === 'true';
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }

  if (!serviceWorkerRegistrationPromise) {
    serviceWorkerRegistrationPromise = navigator.serviceWorker
      .register('/sw.js')
      .catch((error) => {
        console.warn('Service worker registration failed:', error);
        return null;
      });
  }

  return serviceWorkerRegistrationPromise;
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (typeof window === 'undefined' || !('Notification' in window) || !areNotificationsEnabled()) {
    return false;
  }

  if (Notification.permission === 'granted') return true;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

export function showNotification(title: string, body: string, icon?: string, config?: ShowNotificationConfig) {
  if (
    typeof window === 'undefined' ||
    typeof document === 'undefined' ||
    !('Notification' in window) ||
    !areNotificationsEnabled()
  ) {
    return;
  }

  if (Notification.permission !== 'granted') return;
  if (document.hasFocus() && !config?.force) return;

  const url = config?.url || '/';
  const options: NotificationOptions = {
    body,
    icon: icon || '/ally-icon.png',
    tag: config?.tag || DEFAULT_NOTIFICATION_TAG,
    data: { url },
  };

  if ('serviceWorker' in navigator) {
    void navigator.serviceWorker.ready
      .then(async (registration) => {
        if (registration?.showNotification) {
          // Notification actions are only supported on persistent
          // (service-worker) notifications — attaching them to a
          // `new Notification(...)` throws a TypeError per spec.
          await registration.showNotification(title, {
            ...options,
            actions: [{ action: 'open', title: 'Open conversation' }],
          } as NotificationOptions);
          return;
        }
        new Notification(title, options);
      })
      .catch(() => {
        new Notification(title, options);
      });
    return;
  }

  new Notification(title, options);
}

export async function primeNotifications() {
  if (
    typeof window === 'undefined' ||
    !('Notification' in window) ||
    !areNotificationsEnabled() ||
    Notification.permission !== 'default'
  ) {
    return;
  }

  try {
    await requestNotificationPermission();
  } catch (error) {
    console.warn('Notification permission request failed:', error);
  }
}
